// GridScaffold.jsx — single file
import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * GridScaffold with double-rail outer borders + helpers:
 *  - Double vertical rails at content edges
 *  - Optional diagonal-hatched gutters outside rails
 *  - Optional inner vertical grid/rows/dots
 *  - <SectionFrame> draws a full rectangle frame with double borders, aligned to rails
 *  - <RailBand> produces rail-snapped separators (distributed borders)
 *
 * Usage:
 * <SectionFrame>
 *   <YourSection/>
 * </SectionFrame>
 *
 * <RailBand dotted> ... </RailBand>
 */

const GridScaffold = ({
  // Container
  containerMax = "max-w-7xl",
  containerPx = 24,

  // Rails (outer vertical)
  railColor = "#0f172a",
  railOpacity = 0.22,
  railWidth = 1,
  railStyle = "solid", // solid | dotted | dashed
  railDouble = true,
  railDoubleGap = 6, // px between the two vertical borders
  showLeftRails = true,
  showRightRails = true,

  // Inner vertical grid (inside content)
  showSecondaryGrid = false,
  gridGap = 120,
  gridColor = "#0f172a",
  gridOpacity = 0.06,
  gridWidth = 1,

  // Horizontal rows (inside content)
  showRows = false,
  rowStep = 120,
  rowColor = "#0f172a",
  rowOpacity = 0.06,
  rowWidth = 1,

  // Optional dot overlay
  showDotOverlay = false,
  dotGapX = 24,
  dotGapY = 24,
  dotDiameter = 1,

  // Diagonal hatch in gutters (outside rails)
  showHatchGutters = true,
  hatchAngleDeg = 135,
  hatchGap = 16,
  hatchWidth = 1,
  hatchColor = "#0f172a",
  hatchOpacity = 0.12,
  showLeftHatchGutter = true,
  showRightHatchGutter = true,

  // Responsive
  hideOnMobile = false,

  className = "",
}) => {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const [rails, setRails] = useState({ left: 0, right: 0 });

  useLayoutEffect(() => {
    if (!wrapperRef.current || !containerRef.current) return;
    const update = () => {
      const w = wrapperRef.current.getBoundingClientRect();
      const c = containerRef.current.getBoundingClientRect();
      const leftRail = c.left - w.left + containerPx;
      const rightRail = c.right - w.left - containerPx;
      setRails({ left: Math.round(leftRail), right: Math.round(rightRail) });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.documentElement);
    ro.observe(wrapperRef.current);
    ro.observe(containerRef.current);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, [containerPx]);

  const railStroke =
    railStyle === "dotted"
      ? `${railWidth}px dotted rgba(${hexToRgb(railColor)}, ${railOpacity})`
      : railStyle === "dashed"
      ? `${railWidth}px dashed rgba(${hexToRgb(railColor)}, ${railOpacity})`
      : `${railWidth}px solid rgba(${hexToRgb(railColor)}, ${railOpacity})`;

  return (
    <div
      ref={wrapperRef}
      className={`pointer-events-none absolute inset-0 ${hideOnMobile ? "hidden md:block" : ""} ${className}`}
      aria-hidden="true"
    >
      {/* Ruler container that defines rail positions */}
      <div
        ref={containerRef}
        className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-full ${containerMax} mx-auto`}
        style={{ paddingLeft: containerPx, paddingRight: containerPx }}
      >
        {/* Inner vertical grid */}
        {showSecondaryGrid && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(90deg,
                rgba(${hexToRgb(gridColor)}, ${gridOpacity}) 0,
                rgba(${hexToRgb(gridColor)}, ${gridOpacity}) ${gridWidth}px,
                transparent ${gridWidth}px,
                transparent ${gridGap}px)`,
            }}
          />
        )}

        {/* Horizontal rows */}
        {showRows && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(180deg,
                rgba(${hexToRgb(rowColor)}, ${rowOpacity}) 0,
                rgba(${hexToRgb(rowColor)}, ${rowOpacity}) ${rowWidth}px,
                transparent ${rowWidth}px,
                transparent ${rowStep}px)`,
            }}
          />
        )}

        {/* Dot overlay */}
        {showDotOverlay && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at center,
                rgba(${hexToRgb(gridColor)}, ${gridOpacity}) ${dotDiameter}px,
                transparent ${dotDiameter}px)`,
              backgroundSize: `${dotGapX}px ${dotGapY}px`,
            }}
          />
        )}

        {/* Primary rails */}
        {showLeftRails && (
          <div className="absolute inset-y-0" style={{ left: 0, borderLeft: railStroke }} />
        )}
        {showRightRails && (
          <div className="absolute inset-y-0" style={{ right: 0, borderRight: railStroke }} />
        )}

        {/* Double rails (offset inward) */}
        {railDouble && (
          <>
            {showLeftRails && (
              <div
                className="absolute inset-y-0"
                style={{ left: railDoubleGap, borderLeft: railStroke }}
              />
            )}
            {showRightRails && (
              <div
                className="absolute inset-y-0"
                style={{ right: railDoubleGap, borderRight: railStroke }}
              />
            )}
          </>
        )}
      </div>

{showHatchGutters && rails.right > rails.left && (
  <>
    {/* Left gutter: from wrapper left up to the OUTER rail at the container edge */}
    {showLeftHatchGutter && (
      <div
        className="absolute inset-y-0"
        style={{
          left: 0,
          // width to the container's OUTER rail (rails.left includes containerPx)
          width: Math.max(0, rails.left - containerPx),
          // Grid pattern: vertical lines + horizontal lines + diagonal hatches
          backgroundImage: `
            repeating-linear-gradient(90deg,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity}) 0,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity}) ${hatchWidth}px,
              transparent ${hatchWidth}px,
              transparent ${hatchGap}px),
            repeating-linear-gradient(0deg,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity * 0.7}) 0,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity * 0.7}) ${hatchWidth}px,
              transparent ${hatchWidth}px,
              transparent ${hatchGap}px),
            repeating-linear-gradient(${hatchAngleDeg}deg,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity * 0.5}) 0,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity * 0.5}) ${hatchWidth}px,
              transparent ${hatchWidth}px,
              transparent ${Math.floor(hatchGap * 0.6)}px)`,
        }}
      />
    )}

    {/* Right gutter: from the container's OUTER rail to the wrapper right */}
    {showRightHatchGutter && (
      <div
        className="absolute inset-y-0"
        style={{
          // start at the outer right rail
          left: rails.right + containerPx,
          right: 0,
          // Grid pattern: vertical lines + horizontal lines + diagonal hatches
          backgroundImage: `
            repeating-linear-gradient(90deg,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity}) 0,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity}) ${hatchWidth}px,
              transparent ${hatchWidth}px,
              transparent ${hatchGap}px),
            repeating-linear-gradient(0deg,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity * 0.7}) 0,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity * 0.7}) ${hatchWidth}px,
              transparent ${hatchWidth}px,
              transparent ${hatchGap}px),
            repeating-linear-gradient(${-hatchAngleDeg}deg,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity * 0.5}) 0,
              rgba(${hexToRgb(hatchColor)}, ${hatchOpacity * 0.5}) ${hatchWidth}px,
              transparent ${hatchWidth}px,
              transparent ${Math.floor(hatchGap * 0.6)}px)`,
        }}
      />
    )}
  </>
)}

    </div>
  );
};

/** Helper: full rectangular frame with double borders aligned to rails */
export const SectionFrame = ({
  containerMax = "max-w-7xl",
  containerPx = 24,
  railColor = "#0f172a",
  railOpacity = 0.22,
  railWidth = 1,
  railStyle = "solid",
  doubleGap = 6,
  children,
  className = "",
  inset = 0, // optional inner padding between frame and content
}) => {
  const stroke =
    railStyle === "dotted"
      ? `${railWidth}px dotted rgba(${hexToRgb(railColor)}, ${railOpacity})`
      : railStyle === "dashed"
      ? `${railWidth}px dashed rgba(${hexToRgb(railColor)}, ${railOpacity})`
      : `${railWidth}px solid rgba(${hexToRgb(railColor)}, ${railOpacity})`;

  return (
    <div className={`relative ${className}`}>
      {/* overlay rails renderer */}
      <GridScaffold
        containerMax={containerMax}
        containerPx={containerPx}
        railColor={railColor}
        railOpacity={railOpacity}
        railWidth={railWidth}
        railStyle={railStyle}
        railDouble
        railDoubleGap={doubleGap}
        showSecondaryGrid={false}
        showHatchGutters
        hideOnMobile
      />

      {/* framed box that snaps to rails via negative margins */}
      <div
        className={`mx-auto w-full ${containerMax} relative z-10`}
        style={{ paddingLeft: containerPx, paddingRight: containerPx }}
      >
        <div
          className="relative"
          style={{
            marginLeft: -containerPx - doubleGap,
            marginRight: -containerPx - doubleGap,
            // top + bottom double borders
            borderTop: stroke,
            borderBottom: stroke,
            boxShadow: `inset ${doubleGap}px 0 0 0 transparent`, // keep layout intent clear
          }}
        >
          {/* fake inner top/bottom to create the "double" look */}
          <div
            className="absolute left-0 right-0"
            style={{ top: doubleGap, borderTop: stroke }}
          />
          <div
            className="absolute left-0 right-0"
            style={{ bottom: doubleGap, borderBottom: stroke }}
          />
          <div style={{ padding: inset }}>{children}</div>
        </div>
      </div>
    </div>
  );
};

/** Helper: band that connects to the rails and acts as a distributed separator */
export const RailBand = ({
  containerPx = 24,
  doubleGap = 6,
  dotted = true,
  borderColor = "rgba(15,23,42,0.22)",
  bg = "transparent",
  className = "",
  children,
}) => {
  const border = dotted ? `1px dotted ${borderColor}` : `1px solid ${borderColor}`;
  return (
    <div
      className={`relative z-10 mx-auto w-full ${className}`}
      style={{
        marginLeft: -(containerPx + doubleGap),
        marginRight: -(containerPx + doubleGap),
        borderTop: border,
        borderBottom: border,
        background: bg,
      }}
    >
      <div style={{ paddingLeft: containerPx, paddingRight: containerPx }}>{children}</div>
    </div>
  );
};

/** utilities */
function hexToRgb(hex) {
  let c = hex.replace("#", "");
  if (c.length === 3) c = c.split("").map((ch) => ch + ch).join("");
  const n = parseInt(c, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

export default GridScaffold;
